<script lang="ts">
  import { Checkbox, FormGroup, TextInput, TextArea } from 'carbon-components-svelte';

  import ComboSelect from '../../../components/ComboSelect.svelte';

  import { HH_MM_PATTERN } from '../../../../shared/constants';
  import type { ExistingSchedule } from './sla-task-form-shared';
  import type { SlaTaskFormValues } from '../../../../shared/types/sla-task-types';

  export let errors: Record<string, string> = {},
    touched: Record<string, boolean> = {},
    values: Partial<SlaTaskFormValues> = {},
    disabled = false,
    setValue: (name: string, value: unknown) => void,
    existingSchedules: ExistingSchedule[] = [];

  const timezones = Intl.supportedValuesOf('timeZone').map((tz) => ({ id: tz, text: tz }));

  function onInput(key: string) {
    return function handleChange(event: Event): void {
      const target = event.target as HTMLInputElement | null;
      const detail = (event as CustomEvent).detail;

      setValue(key, target?.value ?? detail?.selectedId ?? detail ?? null);
    };
  }

  function timeToMinutes(value: unknown): number | null {
    if (typeof value !== 'string' || !HH_MM_PATTERN.test(value)) {
      return null;
    }

    const [hours, minutes] = value.split(':');
    return Number(hours) * 60 + Number(minutes);
  }

  $: currentTimeInMinutes = timeToMinutes(values.time);
  // TODO: cross-timezone comparison?
  $: existingScheduleCloseBy =
    currentTimeInMinutes !== null &&
    !!values.timezone &&
    existingSchedules.some((schedule) => {
      const current = currentTimeInMinutes;
      const scheduleTimeInMinutes = timeToMinutes(schedule.time);

      if (current === null || scheduleTimeInMinutes === null || schedule.timezone !== values.timezone) {
        return false;
      }

      const diffMinutes = Math.abs(scheduleTimeInMinutes - current);

      return diffMinutes > 0 && diffMinutes < 30;
    });
  $: shouldWarnAboutSchedule = !errors.time && !errors.timezone && existingScheduleCloseBy;
</script>

<div class="form-group">
  <FormGroup>
    <TextInput
      required
      name="friendlyName"
      labelText="Task Name"
      placeholder="Enter a task name"
      value={values.friendlyName ?? ''}
      invalid={Boolean(touched.friendlyName && errors.friendlyName)}
      invalidText={errors.friendlyName ?? ''}
      readonly={disabled}
      autocomplete="off"
      on:change={onInput('friendlyName')}
    />
    <TextArea
      name="description"
      labelText="Description"
      placeholder="Enter a description (optional)"
      value={values.description ?? ''}
      rows={2}
      {disabled}
      on:change={onInput('description')}
    />

    <Checkbox
      name="internalOnly"
      labelText="Internal only"
      checked={values.internalOnly ?? false}
      {disabled}
      on:check={onInput('internalOnly')}
    />
  </FormGroup>
</div>
<div class="form-group" class:warning-input={shouldWarnAboutSchedule}>
  <FormGroup>
    <TextInput
      required
      type="time"
      name="time"
      labelText="Time"
      value={values.time ?? ''}
      invalid={Boolean(touched.time && errors.time)}
      invalidText={errors.time ?? ''}
      on:input={onInput('time')}
    />
    {#if shouldWarnAboutSchedule}
      <div class="warning-text">
        This task's schedule is close to an existing task's schedule. Please review if they can be combined instead.
      </div>
    {/if}
    <ComboSelect
      required
      labelText="Time zone"
      items={timezones}
      name="timezone"
      placeholder="Time zone"
      selectedId={values.timezone ?? null}
      invalid={Boolean(touched.timezone && errors.timezone)}
      invalidText={errors.timezone ?? ''}
      on:change={onInput('timezone')}
      let:item
    >
      {item.text}
    </ComboSelect>
  </FormGroup>
</div>

<style>
  .form-group {
    margin-bottom: 1rem;
  }

  .warning-input :global(.bx--text-input) {
    outline: 2px solid var(--cds-inverse-support-03);
    outline-offset: -2px;
  }

  .warning-text {
    color: var(--cds-inverse-support-03);
    font-size: 0.75rem;
  }
</style>
